import { DateAsString, SporsmalType, UtleggType } from '../../types/generated-types';
import { toValidNumberString } from '../format';
import { CurrentSporsmal, CurrentUtleggslinje } from '../reducer/current-steg';
import { Utleggsporsmal } from '../reducer/sporsmalsflyt-types';
import { UpdateUtleggslinjeData, UtleggslinjeFelt } from './utlegg';

export interface CurrentUtleggsporsmal {
    type: SporsmalType.Utlegg;
    sporsmal: Utleggsporsmal;
    utleggslinjer: CurrentUtleggslinje[];
}

export type BesvartUtleggsporsmal = CurrentUtleggsporsmal;

export const isUtleggSporsmal = (currentSporsmal: CurrentSporsmal): currentSporsmal is CurrentUtleggsporsmal =>
    currentSporsmal && currentSporsmal.type === SporsmalType.Utlegg;

export const utleggslinjeContainsInformation = (utleggslinje: CurrentUtleggslinje) =>
    utleggslinje.beskrivelse || utleggslinje.utgift || utleggslinje.refundert;

const compareDatoOnUtleggslinjer = (dato1: DateAsString, dato2: DateAsString) =>
    new Date(dato1).getTime() - new Date(dato2).getTime();

export const earliestUtleggDato = (besvartUtleggsporsmal: BesvartUtleggsporsmal): DateAsString | undefined =>
    besvartUtleggsporsmal.utleggslinjer.map((linje) => linje.dato).sort(compareDatoOnUtleggslinjer)[0];

const updateUtleggslinje = (
    opprinneligUtleggslinje: CurrentUtleggslinje,
    updatedInfo: UpdateUtleggslinjeData,
): CurrentUtleggslinje => {
    switch (updatedInfo.felt) {
        case UtleggslinjeFelt.Beskrivelse:
            return { ...opprinneligUtleggslinje, beskrivelse: updatedInfo.updatedData };
        case UtleggslinjeFelt.Utgift:
            return { ...opprinneligUtleggslinje, utgift: toValidNumberString(updatedInfo.updatedData) };
        case UtleggslinjeFelt.Refundert:
            return { ...opprinneligUtleggslinje, refundert: toValidNumberString(updatedInfo.updatedData) };
        case UtleggslinjeFelt.DagensPris:
            return { ...opprinneligUtleggslinje, dagensPris: toValidNumberString(updatedInfo.updatedData) };
        default:
            return opprinneligUtleggslinje;
    }
};

export const replaceUtleggslinje = (updatedInfo: UpdateUtleggslinjeData, utleggslinjer: CurrentUtleggslinje[]) => {
    return utleggslinjer.map((linje) => {
        if (linje.id === updatedInfo.id) {
            return updateUtleggslinje(linje, updatedInfo);
        }
        return linje;
    });
};

export const updateCurrentUtleggSporsmal = (
    currentSporsmal: CurrentSporsmal,
    utleggslinje: UpdateUtleggslinjeData,
): CurrentSporsmal => {
    if (isUtleggSporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            utleggslinjer: replaceUtleggslinje(utleggslinje, currentSporsmal.utleggslinjer),
        };
    }
    return currentSporsmal;
};

export const updateDatoInCurrentUtleggSporsmal = (
    currentSporsmal: CurrentSporsmal,
    utleggslinjeId: number,
    dato: DateAsString,
): CurrentSporsmal => {
    if (isUtleggSporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            utleggslinjer: currentSporsmal.utleggslinjer.map((linje) => {
                if (linje.id === utleggslinjeId) {
                    return {
                        ...linje,
                        dato,
                    };
                }
                return linje;
            }),
        };
    }
    return currentSporsmal;
};

export const emptyUtleggslinje = (utleggType: UtleggType): CurrentUtleggslinje => ({
    beskrivelse: '',
    dato: undefined,
    utgift: '',
    refundert: '',
    id: 0,
    utleggType,
    dagensPris: '',
});

const nextUtleggslinjeId = (utleggslinjer: CurrentUtleggslinje[]): number => {
    if (utleggslinjer.length === 0) {
        return 0;
    }
    const highestId = Math.max(...utleggslinjer.map((utleggslinje) => utleggslinje.id));
    return highestId + 1;
};

export const addUtleggslineToCurrent = (currentSporsmal: CurrentSporsmal): CurrentSporsmal => {
    if (isUtleggSporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            utleggslinjer: [
                ...currentSporsmal.utleggslinjer,
                {
                    ...emptyUtleggslinje(currentSporsmal.sporsmal.utleggType),
                    id: nextUtleggslinjeId(currentSporsmal.utleggslinjer),
                },
            ],
        };
    }
    return currentSporsmal;
};
